<script setup lang="ts">
import { ArrowRight, ChevronDown } from 'lucide-vue-next'

import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'
import { SEGMENT_DETAILS_I18N_PREFIX, buildSegmentChanges, buildSegmentConditions } from './segmentActivityDetails'

const { t, locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const getLocalizedText = (
  value: FeedActivity['title'] | FeedActivity['description'] | string | null | undefined
): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[locale.value as keyof typeof value] || value.en || ''
  }
  return value
}

const isJoin = computed(() => {
  return props.activity.type === 'segment.join'
})

const segmentType = computed(() => {
  return props.activity.data?.segment_type || 'dynamic'
})

const segmentName = computed(() => {
  return props.activity.data?.segment_name || ''
})

const descriptionText = computed(() => {
  return getLocalizedText(props.activity.description)
})

const translationKey = computed(() => {
  const action = isJoin.value ? 'join' : 'leave'
  const type = segmentType.value === 'static' ? 'list' : segmentType.value === 'dynamic' ? 'segment' : 'default'

  return `pages.customer.customerDetailPage.blocks.activity.segment.text.${action}.${type}`
})

const title = computed(() => {
  return getLocalizedText(props.activity.title) || ''
})

const icon = computed(() => {
  if (segmentType.value === 'static') {
    return props.activity.icon || 'IconoirNotes'
  }
  if (isJoin.value) {
    return props.activity.icon || 'HeroArrowLeftOnRectangleIconOutline'
  }
  return props.activity.icon || 'HeroArrowRightOnRectangleIconOutline'
})

const iconColor = computed(() => {
  if (isJoin.value) {
    return 'bg-indigo-500'
  }
  return 'bg-orange-500'
})

const conditions = computed(() =>
  buildSegmentConditions(t, locale.value, props.activity.additional_data?.segment_rules)
)

const changes = computed(() => buildSegmentChanges(t, locale.value, props.activity.additional_data?.changed_attributes))

const hasDetails = computed(() => conditions.value.length > 0 || changes.value.length > 0)

const isDetailsOpen = ref(false)

const accentClasses = computed(() =>
  isJoin.value
    ? { arrow: 'text-indigo-500', box: 'border-indigo-200 bg-indigo-50', label: 'text-indigo-700' }
    : { arrow: 'text-orange-500', box: 'border-orange-200 bg-orange-50', label: 'text-orange-700' }
)

const detailsId = computed(() => `segment-activity-details-${props.activity.id}`)
</script>

<template>
  <ActivityCardBase
    :icon="icon"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template v-if="title" #title>
      <p class="text-sm font-medium text-foreground">{{ title }}</p>
    </template>
    <template #description>
      <p
        v-if="descriptionText"
        :class="[
          'text-sm text-muted-foreground',
          {
            'mt-1': title
          }
        ]"
      >
        {{ descriptionText }}
      </p>
      <i18n-t
        v-else
        :keypath="translationKey"
        tag="p"
        :class="[
          'text-sm text-muted-foreground',
          {
            'mt-1': title
          }
        ]"
      >
        <template #segmentName>
          <span class="font-medium">{{ segmentName }}</span>
        </template>
      </i18n-t>
    </template>
    <template v-if="hasDetails" #meta>
      <button
        type="button"
        class="mt-0.5 flex cursor-pointer items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        :aria-expanded="isDetailsOpen"
        :aria-controls="detailsId"
        @click="isDetailsOpen = !isDetailsOpen"
      >
        <span>{{ t(`${SEGMENT_DETAILS_I18N_PREFIX}.toggle`) }}</span>
        <ChevronDown class="size-4 transition-transform duration-200" :class="{ 'rotate-180': isDetailsOpen }" />
      </button>
    </template>
    <template v-if="hasDetails" #footer>
      <div
        v-if="isDetailsOpen"
        :id="detailsId"
        class="ml-8 mt-4 flex flex-col gap-4 self-stretch rounded-lg border bg-white p-4"
      >
        <div v-if="conditions.length">
          <p class="text-sm font-medium text-muted-foreground">
            {{ t(`${SEGMENT_DETAILS_I18N_PREFIX}.conditions`) }}
          </p>
          <div class="mt-2 flex flex-wrap gap-1.5">
            <BaseChip
              v-for="condition in conditions"
              :key="condition.key"
              class="whitespace-normal"
              :text="condition.label"
            >
              <template #label>
                <span class="font-normal">{{ condition.label }}</span>
                <span v-if="condition.operator" class="font-normal text-muted-foreground">{{
                  condition.operator
                }}</span>
                <span>{{ condition.value }}</span>
              </template>
            </BaseChip>
          </div>
        </div>
        <div v-if="changes.length">
          <p class="text-sm font-medium text-muted-foreground">
            {{ t(`${SEGMENT_DETAILS_I18N_PREFIX}.changes`) }}
          </p>
          <div class="mt-2 flex flex-col gap-1.5">
            <div v-for="change in changes" :key="change.key" class="flex items-stretch gap-1.5">
              <div class="flex min-w-0 flex-1 flex-col justify-center rounded-lg border bg-stone-100 px-2 py-1.5">
                <span class="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {{ change.label }} · {{ t(`${SEGMENT_DETAILS_I18N_PREFIX}.before`) }}
                </span>
                <span class="text-xs font-semibold leading-[18px] text-foreground">{{ change.before }}</span>
              </div>
              <div class="flex shrink-0 items-center justify-center self-center px-0.5">
                <ArrowRight class="size-4" :class="accentClasses.arrow" />
              </div>
              <div
                class="flex min-w-0 flex-1 flex-col justify-center rounded-lg border px-2 py-1.5"
                :class="accentClasses.box"
              >
                <span class="mb-0.5 text-[10px] font-semibold uppercase tracking-wider" :class="accentClasses.label">
                  {{ t(`${SEGMENT_DETAILS_I18N_PREFIX}.after`) }}
                </span>
                <span class="text-xs font-semibold leading-[18px] text-foreground">{{ change.after }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ActivityCardBase>
</template>
